import { useState, useEffect } from "react";
import { ScrollText, LogIn, UserPlus, UserMinus, KeyRound, Settings, Trash2, Download, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_CONFIG = {
  login: { icon: LogIn, color: "text-blue-600", bg: "bg-blue-50", label: "Login" },
  create_admin: { icon: UserPlus, color: "text-green-600", bg: "bg-green-50", label: "Created Admin" },
  delete_admin: { icon: UserMinus, color: "text-red-600", bg: "bg-red-50", label: "Deleted Admin" },
  reset_password: { icon: KeyRound, color: "text-amber-600", bg: "bg-amber-50", label: "Reset Password" },
  change_password: { icon: KeyRound, color: "text-purple-600", bg: "bg-purple-50", label: "Changed Password" },
  update_cost_config: { icon: Settings, color: "text-slate-600", bg: "bg-slate-50", label: "Updated Config" },
  reset_analytics: { icon: Trash2, color: "text-red-600", bg: "bg-red-50", label: "Reset Analytics" },
  export_data: { icon: Download, color: "text-teal-600", bg: "bg-teal-50", label: "Exported Data" },
};

const formatTimestamp = (ts) => {
  try {
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "Just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: d.getFullYear() !== now.getFullYear() ? "numeric" : undefined });
  } catch { return ts; }
};

export const AuditLog = ({ token }) => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState(true);
  const [loading, setLoading] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/audit-log?limit=50`, { headers });
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { fetchLogs(); }, []);

  return (
    <Card className="mb-6" data-testid="audit-log-panel">
      <CardHeader className="flex flex-row items-center justify-between cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <CardTitle className="text-lg flex items-center gap-2">
          <ScrollText size={18} className="text-[#518dca]" /> Audit Log
          <span className="text-xs font-normal text-[#999] ml-1">{total} events</span>
        </CardTitle>
        <Button variant="ghost" size="sm" data-testid="audit-log-toggle">
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </Button>
      </CardHeader>
      {expanded && (
        <CardContent>
          {loading ? (
            <div className="text-center py-4 text-sm text-[#999]">Loading...</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-6 text-sm text-[#999]">No audit events yet</div>
          ) : (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200" />
              <div className="space-y-1">
                {logs.map((log, idx) => {
                  const cfg = ACTION_CONFIG[log.action] || { icon: ScrollText, color: "text-gray-600", bg: "bg-gray-50", label: log.action };
                  const Icon = cfg.icon;
                  return (
                    <div key={idx} className={`relative flex items-start gap-3 pl-9 py-2 rounded-lg hover:bg-gray-50 transition-colors`} data-testid={`audit-entry-${idx}`}>
                      <div className={`absolute left-2 top-2.5 w-5 h-5 rounded-full ${cfg.bg} flex items-center justify-center ring-2 ring-white`}>
                        <Icon size={11} className={cfg.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs font-semibold px-1.5 py-0.5 rounded ${cfg.bg} ${cfg.color}`}>{cfg.label}</span>
                          <span className="text-xs font-medium text-[#333]">{log.admin}</span>
                          {log.target && <span className="text-xs text-[#999]">&#8594; {log.target}</span>}
                          <span className="text-xs text-[#bbb] ml-auto flex-shrink-0">{formatTimestamp(log.timestamp)}</span>
                        </div>
                        {log.detail && <p className="text-xs text-[#666] mt-0.5 truncate">{log.detail}</p>}
                        {log.records_deleted !== undefined && <p className="text-xs text-red-500 mt-0.5">{log.records_deleted} records deleted</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
              {total > 50 && (
                <p className="text-xs text-center text-[#999] mt-3 pt-2 border-t">Showing latest 50 of {total} events</p>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};
